import { createContext, useContext, useMemo, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { X, CalendarCheck, Send, ArrowLeft, ArrowRight, ShieldCheck } from "lucide-react";
import { COURSES, BOOKING_POLICY, upcomingDates, activeCourses } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BookingCtx = createContext(null);
export const useBooking = () => useContext(BookingCtx);

const datesFor = (courseTitle) => {
  const c = COURSES.find((x) => x.title === courseTitle);
  if (!c) return [];
  const list = upcomingDates(c.id).map((d) => d.date);
  return list.length ? list : ["Da concordare — ti proponiamo noi le date"];
};

const emptyForm = (course = "", date = "") => ({
  course: course || activeCourses()[0]?.title || COURSES[0].title,
  date: date || datesFor(course || COURSES[0].title)[0] || "",
  surname: "",
  name: "",
  birth_place: "",
  birth_date: "",
  gender: "",
  fiscal_code: "",
  email: "",
  phone: "",
  notes: "",
});

const steps = ["Corso", "Dati", "Conferma"];
const CF_MONTHS = ["A", "B", "C", "D", "E", "H", "L", "M", "P", "R", "S", "T"];
const EVEN = { ...Object.fromEntries("ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((c, i) => [c, i])), ...Object.fromEntries("0123456789".split("").map((c, i) => [c, i])) };
const ODD = { A:1,B:0,C:5,D:7,E:9,F:13,G:15,H:17,I:19,J:21,K:2,L:4,M:18,N:20,O:11,P:3,Q:6,R:8,S:12,T:14,U:16,V:10,W:22,X:25,Y:24,Z:23, 0:1,1:0,2:5,3:7,4:9,5:13,6:15,7:17,8:19,9:21 };
const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().replace(/[^A-Z]/g, "");
const cons = (s) => norm(s).replace(/[AEIOU]/g, "");
const vows = (s) => norm(s).replace(/[^AEIOU]/g, "");
const pad3 = (s) => (s + "XXX").slice(0, 3);
const surnameCode = (s) => pad3(cons(s) + vows(s));
const nameCode = (s) => {
  const c = cons(s);
  if (c.length >= 4) return c[0] + c[2] + c[3];
  return pad3(c + vows(s));
};
const cfCheck = (first15) => {
  let sum = 0;
  for (let i = 0; i < 15; i += 1) sum += (i + 1) % 2 === 0 ? EVEN[first15[i]] : ODD[first15[i]];
  return String.fromCharCode(65 + (sum % 26));
};
const parseBirthDate = (value) => {
  if (!value) return null;
  let y;
  let m;
  let d;
  if (value.includes("-")) {
    const p = value.split("-");
    if (p[0].length === 4) [y, m, d] = p.map(Number);
    else [d, m, y] = p.map(Number);
  } else {
    const p = value.split(/[\/.]/).map(Number);
    [d, m, y] = p;
  }
  if (!y || !m || !d || m < 1 || m > 12 || d < 1 || d > 31) return null;
  return { y, m, d };
};
const dateIT = (value) => {
  const p = parseBirthDate(value);
  return p ? `${String(p.d).padStart(2, "0")}/${String(p.m).padStart(2, "0")}/${p.y}` : "";
};
const calcCF = ({ surname, name, birth_date, birth_place, gender }, comuniMap) => {
  const p = parseBirthDate(birth_date);
  if (!surname || !name || !p || !birth_place || !gender) return "";
  const day = String(p.d + (gender === "F" ? 40 : 0)).padStart(2, "0");
  const placeName = birth_place.replace(/\s*\([A-Z]{2}\)\s*$/, "");
  const placeKey = norm(placeName);
  const placeCode = comuniMap ? comuniMap[placeKey] : null;
  const base = `${surnameCode(surname)}${nameCode(name)}${String(p.y).slice(-2)}${CF_MONTHS[p.m - 1]}${day}`;
  if (!placeCode) return `${base}XXXX`;
  const first15 = `${base}${placeCode}`;
  return first15 + cfCheck(first15);
};

const Field = ({ label, required = false, children, hint }) => (
  <label className="block rounded-2xl border border-brand-black/12 bg-brand-paper px-4 py-3 focus-within:border-brand-red focus-within:bg-white transition-colors">
    <span className="mb-1.5 flex items-center justify-between gap-3 text-[11px] font-bold uppercase tracking-[0.16em] text-brand-ink">
      <span>{label} {required && <span className="text-brand-red">*</span>}</span>
      {hint && <span className="normal-case tracking-normal text-brand-ink/55 text-right">{hint}</span>}
    </span>
    {children}
  </label>
);

export const BookingProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);
  const [comuni, setComuni] = useState(null);
  const lastAutoCF = useRef("");

  useEffect(() => {
    if (!open || comuni) return;
    fetch("/data/comuni-cf.json")
      .then((r) => r.json())
      .then((list) => {
        const map = {};
        const options = [];
        list.forEach(({ n, c, p }) => {
          const key = norm(n);
          if (!map[key]) map[key] = c;
          options.push(`${n} (${p})`);
        });
        setComuni({ map, options });
      })
      .catch(() => {});
  }, [open, comuni]);

  const openBooking = (course = "", date = "") => {
    setForm(emptyForm(course, date));
    setConsent(false);
    setStep(0);
    setOpen(true);
  };

  const ctx = useMemo(() => ({ openBooking }), []);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setCourse = (course) => setForm((f) => ({ ...f, course, date: datesFor(course)[0] || "" }));

  useEffect(() => {
    if (open) setForm((f) => ({ ...f, date: datesFor(f.course).includes(f.date) ? f.date : (datesFor(f.course)[0] || "") }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.course]);

  useEffect(() => {
    const auto = calcCF(form, comuni?.map);
    if (!auto) return;
    const canOverwrite = !form.fiscal_code || form.fiscal_code === lastAutoCF.current || form.fiscal_code.endsWith("XXXX");
    if (!canOverwrite) return;
    if (form.fiscal_code === auto) return;
    lastAutoCF.current = auto;
    setForm((f) => ({ ...f, fiscal_code: auto }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.surname, form.name, form.birth_date, form.birth_place, form.gender, comuni]);

  const validStep = () => {
    if (step === 0) return Boolean(form.course && form.date);
    if (step === 1) return Boolean(form.surname && form.name && form.birth_place && form.birth_date && form.gender && form.fiscal_code && !form.fiscal_code.endsWith("XXXX") && form.email);
    return consent;
  };

  const next = () => {
    if (!validStep()) {
      toast.error(step === 1 && form.fiscal_code.endsWith("XXXX") ? "Comune non riconosciuto: completa il codice fiscale" : step === 2 ? "Devi autorizzare il trattamento dei dati GDPR" : "Completa i campi obbligatori per continuare");
      return;
    }
    setStep((s) => Math.min(2, s + 1));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (step !== 2) {
      next();
      return;
    }
    if (!consent) {
      toast.error("Devi autorizzare il trattamento dei dati GDPR 679/2016");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        course: form.course,
        date: form.date,
        name: `${form.surname} ${form.name}`.trim(),
        birth_info: `${form.birth_place}, ${dateIT(form.birth_date)}`,
        fiscal_code: form.fiscal_code,
        email: form.email,
        phone: form.phone,
        notes: form.notes,
      };
      await axios.post(`${API}/booking`, payload, { timeout: 10000 });
      toast.success("Registrazione inviata! Ti arriva la mail di avvenuta registrazione.", { duration: 4500 });
      setForm(emptyForm());
      setConsent(false);
      setStep(0);
      setOpen(false);
    } catch {
      toast.error("Errore. Riprova o scrivici su WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full bg-transparent text-brand-black placeholder:text-brand-ink/45 focus:outline-none text-base";
  const cfUnknown = form.fiscal_code.endsWith("XXXX");
  const today = new Date().toISOString().slice(0, 10);

  return (
    <BookingCtx.Provider value={ctx}>
      {children}
      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-0 sm:p-4"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              data-testid="booking-modal"
            >
              <div className="absolute inset-0 bg-brand-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
              <motion.form
                onSubmit={submit}
                initial={{ y: 70, opacity: 0, scale: 0.98 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                exit={{ y: 70, opacity: 0, scale: 0.98 }}
                transition={{ type: "spring", stiffness: 260, damping: 28 }}
                className="relative bg-white w-full sm:max-w-3xl border border-border shadow-2xl h-[100dvh] sm:h-auto sm:max-h-[92dvh] rounded-t-[28px] sm:rounded-[28px] flex flex-col overflow-hidden"
              >
                <div className="bg-brand-black text-white px-5 md:px-7 pt-4 pb-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <CalendarCheck size={20} className="text-brand-red shrink-0" />
                      <div className="min-w-0">
                        <span className="font-heading font-bold text-lg block leading-tight truncate">Iscrizione corso</span>
                        <span className="text-[11px] tracking-[0.08em] text-white/55">Sede treffe · Frosinone</span>
                      </div>
                    </div>
                    <button type="button" data-testid="booking-close" onClick={() => setOpen(false)} className="text-white/70 hover:text-white shrink-0">
                      <X size={22} />
                    </button>
                  </div>
                  <div className="mt-4">
                    <div className="grid grid-cols-3 text-center text-[10px] sm:text-[11px] uppercase tracking-[0.14em] text-white/55 mb-2">
                      {steps.map((s, i) => <span key={s} className={i <= step ? "text-white" : ""}>{i + 1}. {s}</span>)}
                    </div>
                    <div className="h-[3px] bg-white/15 rounded-full overflow-hidden">
                      <motion.div className="h-full bg-brand-red" animate={{ width: `${((step + 1) / 3) * 100}%` }} />
                    </div>
                  </div>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto px-5 py-5 md:px-8 md:py-6 pb-[calc(1.25rem+env(safe-area-inset-bottom))]">
                  {step === 0 && (
                    <div data-testid="booking-step-0" className="grid gap-6">
                      <div>
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red mb-3">1 · Scegli il corso</div>
                        <div className="grid sm:grid-cols-2 gap-3">
                          {activeCourses().map((c) => (
                            <button
                              type="button"
                              key={c.id}
                              data-testid={`booking-course-card-${c.id}`}
                              onClick={() => setCourse(c.title)}
                              className={`text-left rounded-2xl border p-4 transition-colors ${form.course === c.title ? "border-brand-red bg-brand-red/5" : "border-brand-black/12 bg-white hover:border-brand-black/35"}`}
                            >
                              <div className="font-heading font-bold text-brand-black leading-tight">{c.title}</div>
                              <div className="mt-1 text-xs text-brand-ink">{c.subtitle}</div>
                              <div className="mt-3 text-[11px] uppercase tracking-widest text-brand-ink/70">{c.duration}</div>
                            </button>
                          ))}
                        </div>
                        <select data-testid="booking-course" className="sr-only" value={form.course} onChange={upd("course")}>
                          {activeCourses().map((c) => <option key={c.id} value={c.title}>{c.title}</option>)}
                        </select>
                      </div>
                      <div>
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red mb-3">2 · Scegli la data</div>
                        <div className="flex flex-wrap gap-2">
                          {datesFor(form.course).map((d) => (
                            <button
                              type="button"
                              key={d}
                              data-testid={`booking-date-chip-${d.replace(/[^a-z0-9]+/gi, "-")}`}
                              onClick={() => setForm((f) => ({ ...f, date: d }))}
                              className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${form.date === d ? "border-brand-red bg-brand-red text-white" : "border-brand-black/15 bg-white text-brand-black hover:border-brand-red"}`}
                            >
                              {d}
                            </button>
                          ))}
                        </div>
                        <select data-testid="booking-date" className="sr-only" value={form.date} onChange={upd("date")}>
                          {datesFor(form.course).map((d) => <option key={d} value={d}>{d}</option>)}
                        </select>
                      </div>
                    </div>
                  )}

                  {step === 1 && (
                    <div data-testid="booking-step-1" className="grid gap-4">
                      <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red">3 · I tuoi dati</div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Cognome" required>
                          <input data-testid="booking-surname" autoComplete="family-name" className={input} placeholder="Es. Rossi" value={form.surname} onChange={upd("surname")} />
                        </Field>
                        <Field label="Nome" required>
                          <input data-testid="booking-name" autoComplete="given-name" className={input} placeholder="Es. Stefano" value={form.name} onChange={upd("name")} />
                        </Field>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Luogo di nascita" required hint="comune">
                          <input data-testid="booking-birth-place" list="cf-comuni" className={input} placeholder="Es. Sora" value={form.birth_place} onChange={upd("birth_place")} />
                          <datalist id="cf-comuni">
                            {(comuni?.options || []).map((o) => <option key={o} value={o} />)}
                          </datalist>
                        </Field>
                        <Field label="Data di nascita" required>
                          <input data-testid="booking-birth-date" type="date" max={today} className={input} value={form.birth_date} onChange={upd("birth_date")} />
                        </Field>
                      </div>
                      <Field label="Sesso" required hint="serve per il CF">
                        <select data-testid="booking-gender" className={input} value={form.gender} onChange={upd("gender")}>
                          <option value="">Seleziona</option>
                          <option value="M">M</option>
                          <option value="F">F</option>
                        </select>
                      </Field>
                      <Field label="Codice fiscale" required hint={cfUnknown ? "completa il comune" : "auto-calcolato"}>
                        <input
                          data-testid="booking-fiscal"
                          autoComplete="off"
                          inputMode="text"
                          maxLength={16}
                          className={`${input} uppercase font-mono tracking-[0.12em]`}
                          placeholder="Si compila da solo"
                          value={form.fiscal_code}
                          onChange={upd("fiscal_code")}
                        />
                      </Field>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Indirizzo e-mail" required>
                          <input data-testid="booking-email" type="email" autoComplete="email" className={input} placeholder="nome@email.it" value={form.email} onChange={upd("email")} />
                        </Field>
                        <Field label="Numero di telefono" hint="opzionale">
                          <input data-testid="booking-phone" type="tel" autoComplete="tel" className={input} placeholder="339 ..." value={form.phone} onChange={upd("phone")} />
                        </Field>
                      </div>
                      <Field label="Note" hint="opzionale">
                        <textarea data-testid="booking-notes" rows={2} className={`${input} resize-none`} placeholder="Esigenze particolari, azienda, numero partecipanti…" value={form.notes} onChange={upd("notes")} />
                      </Field>
                    </div>
                  )}

                  {step === 2 && (
                    <div data-testid="booking-step-2" className="grid gap-4">
                      <div data-testid="booking-process" className="relative overflow-hidden rounded-[24px] border border-brand-black bg-brand-black text-white px-5 py-6 md:px-8 md:py-7 text-center">
                        <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_50%_0%,#D62828_0,transparent_42%)]" />
                        <div className="relative">
                          <div className="mx-auto mb-4 w-12 h-12 rounded-full border border-brand-red/60 flex items-center justify-center text-brand-red">
                            <ShieldCheck size={23} />
                          </div>
                          <div className="text-[10px] uppercase tracking-[0.26em] text-brand-red mb-2">Come diventa effettiva</div>
                          <h3 className="font-heading font-black text-2xl md:text-3xl tracking-tighter leading-tight">
                            Questa è una richiesta di iscrizione al corso.
                          </h3>
                          <p className="mt-4 max-w-2xl mx-auto text-white/75 leading-relaxed text-sm md:text-base">
                            {BOOKING_POLICY}
                          </p>
                          <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-2 text-[10px] uppercase tracking-[0.12em]">
                            {["Richiesta", "Mail registrazione", "Copia bonifico", "Conferma corso"].map((t, i) => (
                              <div key={t} className="rounded-full border border-white/15 bg-white/[0.04] px-2.5 py-2 text-white/85">
                                <span className="text-brand-red mr-1">{i + 1}</span> {t}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <label className="flex items-start gap-3 rounded-2xl border border-brand-black/12 bg-brand-paper p-4 text-sm text-brand-ink cursor-pointer">
                        <input type="checkbox" data-testid="booking-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 w-5 h-5 accent-brand-red shrink-0" />
                        <span>Si autorizza al trattamento dei dati in base al GDPR 679/2016. Ho letto l'<Link to="/privacy" className="text-brand-red underline">informativa privacy</Link>. *</span>
                      </label>
                    </div>
                  )}
                </div>

                <div className="border-t border-border bg-white px-5 md:px-8 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))] flex items-center justify-between gap-3">
                  <button
                    type="button"
                    data-testid="booking-back"
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={step === 0}
                    className="inline-flex items-center gap-2 border border-brand-black/15 px-5 py-3 text-sm font-bold uppercase tracking-widest text-brand-black disabled:opacity-40"
                  >
                    <ArrowLeft size={16} /> Indietro
                  </button>
                  {step < 2 ? (
                    <button type="button" data-testid="booking-next" onClick={next} className="inline-flex items-center gap-2 bg-brand-black text-white font-bold uppercase tracking-widest text-sm px-6 py-3 hover:bg-brand-red transition-colors">
                      Avanti <ArrowRight size={16} />
                    </button>
                  ) : (
                    <button type="submit" disabled={loading} data-testid="booking-submit" className="inline-flex items-center gap-2 bg-brand-red text-white font-bold uppercase tracking-widest text-sm px-6 py-3 hover:bg-brand-black transition-colors disabled:opacity-60">
                      {loading ? "Invio…" : <>Invia richiesta <Send size={16} /></>}
                    </button>
                  )}
                </div>
              </motion.form>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </BookingCtx.Provider>
  );
};
